import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import {
  CallTranscript,
  type ICallEventDisplay,
} from '@principle-theorem/principle-core';
import { ICallEvent } from '@principle-theorem/principle-core/interfaces';
import { DATE_TIME_FORMAT, WithRef, toMoment } from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GlobalStoreService } from '../../store/global-store.service';

export interface ICallTranscriptDialogData {
  callEvent: WithRef<ICallEvent>;
  patientName?: string;
}

interface ICallTranscriptTurnView {
  speakerLabel: string;
  text: string;
  isAgent: boolean;
}

interface ICallTranscriptView {
  display: ICallEventDisplay;
  turns: ICallTranscriptTurnView[] | undefined;
}

@Component({
  selector: 'pr-call-transcript-dialog',
  imports: [NgDialogModule],
  templateUrl: './call-transcript-dialog.component.html',
  styleUrl: './call-transcript-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallTranscriptDialogComponent {
  private _data = inject<ICallTranscriptDialogData>(MAT_DIALOG_DATA);
  private _globalStore = inject(GlobalStoreService);

  readonly summary: string | undefined = this._data.callEvent.summary;
  readonly rawTranscript: string = this._data.callEvent.transcript;

  readonly callerIdentity: string =
    this._data.callEvent.callerName ??
    this._data.callEvent.callerPhone ??
    'Unknown caller';

  readonly formattedCreatedAt: string | undefined = this._data.callEvent
    .createdAt
    ? toMoment(this._data.callEvent.createdAt).format(DATE_TIME_FORMAT)
    : undefined;

  readonly view$: Observable<ICallTranscriptView> = this._globalStore
    .getCallEventDisplay$(this._data.callEvent)
    .pipe(
      map((display) => ({ display, turns: this._buildTurns(display.label) }))
    );

  private _buildTurns(
    agentLabel: string
  ): ICallTranscriptTurnView[] | undefined {
    return CallTranscript.parse(this._data.callEvent.transcript)?.map(
      (turn) => ({
        speakerLabel:
          turn.speaker === 'agent'
            ? agentLabel
            : this._data.patientName ??
              this._data.callEvent.callerName ??
              'Caller',
        text: turn.text,
        isAgent: turn.speaker === 'agent',
      })
    );
  }
}
